import { Icons } from '@/assets/icons/icons';
import { cn } from '@/lib/utils';

import { FEED_CHIP_CLASS } from './feedStyles';

import type { CardWatchSuggestion } from '@littlebird/action-cards';

type FeedWatchSuggestionProps = {
  suggestion: CardWatchSuggestion;
  primary: boolean;
  onAccept: () => void;
  onKeep: () => void;
};

const LEAD: Record<CardWatchSuggestion['kind'], string> = {
  complete: 'Looks done',
  dismiss: 'Probably no longer needed',
  working: 'Looks like you’re working on this',
};

const ACCEPT_LABEL: Record<CardWatchSuggestion['kind'], string> = {
  complete: 'Mark done',
  dismiss: 'Dismiss',
  working: 'Move to Working',
};

// What the card's watcher suggests, as the strip across the top of the open card's body, with the user's two
// answers (feed.css joins it to the body). Its reason stays in ⋯ for internal users
// Working is the Feed's blue, as a card's box turns blue while the user works on it; done and dismiss keep the green
const WORKING_TEXT =
  'text-brand-cyan-cornflower-blue dark:text-brand-jordy-blue';

export const FeedWatchSuggestion = ({
  suggestion,
  primary,
  onAccept,
  onKeep,
}: FeedWatchSuggestionProps) => (
  <div
    data-testid="feed-watch-suggestion"
    data-feed-strip=""
    className={cn(
      'flex min-w-0 items-center gap-[calc(10*var(--ac-u))] py-[calc(6*var(--ac-u))] pl-[var(--feed-card-px)] pr-[calc(6*var(--ac-u))] text-[length:calc(12.5*var(--ac-u))] leading-[calc(18*var(--ac-u))]',
      suggestion.kind === 'working' ? WORKING_TEXT : 'text-kind-done'
    )}
  >
    <Icons.circleCheck
      aria-hidden="true"
      className="size-[calc(15*var(--ac-u))] shrink-0"
    />
    <span className="min-w-0 flex-1 truncate font-semibold">
      {LEAD[suggestion.kind]}
    </span>
    <button
      type="button"
      className="h-[calc(24*var(--ac-u))] shrink-0 rounded-full px-[calc(6*var(--ac-u))] text-[length:calc(12.5*var(--ac-u))] font-medium text-foreground-subtle outline-none transition-colors duration-[120ms] hover:text-foreground focus-visible:text-foreground motion-reduce:transition-none"
      onClick={onKeep}
    >
      Keep
    </button>
    <button
      type="button"
      data-card-primary={primary || undefined}
      className={cn(
        'h-[calc(24*var(--ac-u))] shrink-0 whitespace-nowrap rounded-full px-[calc(10*var(--ac-u))] text-[length:calc(12.5*var(--ac-u))] font-medium text-white outline-none transition-opacity duration-[120ms] hover:opacity-90 focus-visible:ring-2 motion-reduce:transition-none',
        suggestion.kind === 'working'
          ? 'bg-brand-cyan-cornflower-blue focus-visible:ring-brand-cyan-cornflower-blue/40'
          : 'bg-kind-done dark:bg-emerald-700 focus-visible:ring-kind-done/40'
      )}
      onClick={onAccept}
    >
      {ACCEPT_LABEL[suggestion.kind]}
    </button>
  </div>
);

export const FeedWatchChip = ({
  kind,
}: {
  kind: CardWatchSuggestion['kind'];
}) => (
  <span
    className={cn(
      FEED_CHIP_CLASS,
      'min-w-0 shrink-[10] truncate',
      kind === 'working'
        ? cn('bg-brand-jordy-blueA/15', WORKING_TEXT)
        : 'bg-emerald-500/10 text-kind-done'
    )}
  >
    {LEAD[kind]}
  </span>
);
